import { GeoCoordinates, GeoSearch } from '@aloft/toolkit-shared';

import mapboxgl from 'mapbox-gl';
import { useEffect, useRef, useState } from 'react';

import { fitBounds } from '../../../modules/referenceView/Tkv2MlsMap/fitBounds.ts';
import MapBox from '../../map/MapBox.ts';
import { MAP_STYLES } from '../../map/constants.ts';
import { useMap } from '../../map/useMap.ts';
import { useRadiusCircle } from './useRadiusCircle.ts';
import { useRedrawPolygon } from './useRedrawPolygon.ts';

const DEFAULT_ZOOM = 14;

export function useFilterCompMap({
  lat,
  lng,
  subjectLat,
  subjectLng,
  geoSearch,
  mapType,
  onPolygonCreateAndUpdate,
  onPolygonDrawDelete,
}: {
  lat: number;
  lng: number;
  subjectLat: number;
  subjectLng: number;
  geoSearch: GeoSearch;
  mapType: string;
  onPolygonCreateAndUpdate: (coords: GeoCoordinates) => void;
  onPolygonDrawDelete: () => void;
}) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapBox | null>(null);
  const propertyLoaded = useRef<boolean>(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  useMap({
    mapContainerRef,
    mapRef,
    center: { lat: subjectLat, lng: subjectLng },
    zoom: DEFAULT_ZOOM,
    enablePolygonDraw: true,
    subject: { lat: subjectLat, lng: subjectLng },
    callbacks: {
      onPinSourceLoad: (sourceId, target) => {
        if (propertyLoaded.current || (lat === subjectLat && lng === subjectLng)) {
          return;
        }
        const source = target.getSource(sourceId) as mapboxgl.GeoJSONSource;

        source.setData({
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [lng, lat],
              },
              properties: { icon: 'Off-Market' },
            },
          ],
        });

        fitBounds(target, [
          { latitude: lat, longitude: lng },
          { latitude: subjectLat, longitude: subjectLng },
        ]);

        propertyLoaded.current = true;
      },
      onMapLoad: () => {
        setMapLoaded(true);
      },
      onPolygonDrawCreate: onPolygonCreateAndUpdate,
      onPolygonDrawUpdate: onPolygonCreateAndUpdate,
      onPolygonDrawDelete: () => {
        mapRef.current?.getDraw().changeMode('draw_polygon');
        onPolygonDrawDelete();
      },
      onStyleLoad: () => {
        setMapLoaded(true);
      },
    },
  });

  useRadiusCircle({
    mapRef,
    subjectLat,
    subjectLng,
    geoSearch,
    mapLoaded,
  });

  useRedrawPolygon({
    mapRef,
    allowPolygonDraw: true,
    geoSearch,
    mapLoaded,
    subjectLat,
    subjectLng,
  });

  useEffect(() => {
    setMapLoaded(false);
    const url = mapType === 'satellite' ? MAP_STYLES.SATELLITE.url : MAP_STYLES.STREET.url;
    mapRef.current?.getMap().setStyle(url);
  }, [mapType]);

  return { mapContainerRef, mapRef };
}
